import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { AlertCircle, ShoppingBag, CreditCard, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';

/**
 * Payment Gateway Page for F Square Media Brands
 * 
 * This page serves as a universal payment gateway for all F Square brands.
 * Brands redirect users here with an order_id, payment happens on mothering.in,
 * then user is redirected back to the brand's site.
 * 
 * Usage from MSocial:
 * 1. Create order via API
 * 2. Redirect to: https://mothering.in/payment?order_id=xxx&return_url=https://msocial.in/success
 * 3. User completes payment
 * 4. Redirected back with: return_url?status=success&order_id=xxx&payment_id=xxx
 */

export default function PaymentGatewayPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const orderId = searchParams.get('order_id');
  const returnUrl = searchParams.get('return_url');
  const apiKey = searchParams.get('api_key'); // Optional: for direct links
  
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [processing, setProcessing] = useState(false);
  const [paymentStatus, setPaymentStatus] = useState(null);
  
  const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://motheringstore.onrender.com';
  
  // Fetch order details
  useEffect(() => {
    if (!orderId) {
      setError('No order ID provided');
      setLoading(false);
      return;
    }
    
    const fetchOrder = async () => {
      try {
        setLoading(true);
        
        // Fetch order status from Payment Proxy API
        const response = await fetch(`${backendUrl}/api/proxy/order-status/${orderId}`, {
          headers: apiKey ? { 'X-API-Key': apiKey } : {}
        });
        
        if (!response.ok) {
          throw new Error('Order not found or invalid');
        }
        
        const data = await response.json();
        
        if (!data.success) {
          throw new Error('Failed to fetch order details');
        }
        
        setOrder(data);
        
        // Check if already paid
        if (data.payment_status === 'paid') {
          setPaymentStatus('already_paid');
        }
        
        setLoading(false);
      } catch (err) {
        console.error('Error fetching order:', err);
        setError(err.message || 'Failed to load order details');
        setLoading(false);
      }
    };
    
    fetchOrder();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderId]);
  
  const handlePayNow = () => {
    if (!order || !order.razorpay_order_id) {
      alert('Order details not available');
      return;
    }
    
    setProcessing(true);
    
    // Load Razorpay script if not already loaded
    if (!window.Razorpay) {
      const script = document.createElement('script');
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => openRazorpayCheckout();
      script.onerror = () => {
        setProcessing(false);
        alert('Failed to load payment gateway. Please try again.');
      };
      document.body.appendChild(script);
    } else {
      openRazorpayCheckout();
    }
  };
  
  const openRazorpayCheckout = () => {
    const options = {
      key: 'rzp_live_wbpfewkItOU4Ly', // Your Razorpay key
      amount: order.amount * 100, // in paise
      currency: order.currency,
      name: 'F Square Media',
      description: `Payment for Order ${order.order_id}`,
      order_id: order.razorpay_order_id,
      
      handler: async function(response) {
        // Payment successful
        await handlePaymentSuccess(response);
      },
      
      prefill: {
        name: order.customer.name,
        email: order.customer.email,
        contact: order.customer.phone
      },
      
      theme: {
        color: '#0ea5e9'
      },
      
      modal: {
        ondismiss: function() {
          setProcessing(false);
        }
      }
    };
    
    const rzp = new window.Razorpay(options);
    rzp.open();
  };
  
  const handlePaymentSuccess = async (razorpayResponse) => {
    try {
      // Verify payment via Payment Proxy API
      const verifyResponse = await fetch(`${backendUrl}/api/proxy/verify-payment`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          razorpay_order_id: razorpayResponse.razorpay_order_id,
          razorpay_payment_id: razorpayResponse.razorpay_payment_id,
          razorpay_signature: razorpayResponse.razorpay_signature
        })
      });
      
      const verifyData = await verifyResponse.json();
      
      if (verifyData.success) {
        setPaymentStatus('success');
        
        // Redirect back to brand's return URL after 2 seconds
        setTimeout(() => {
          redirectToReturnUrl('success', razorpayResponse.razorpay_payment_id);
        }, 2000);
      } else {
        setPaymentStatus('failed');
        setError('Payment verification failed');
      }
      
    } catch (err) {
      console.error('Payment verification error:', err);
      setPaymentStatus('failed');
      setError('Failed to verify payment. Please contact support.');
    } finally {
      setProcessing(false);
    }
  };
  
  const redirectToReturnUrl = (status, paymentId = null) => {
    if (!returnUrl) {
      // No return URL provided, stay on success page
      return;
    }
    
    // Build redirect URL with payment details
    const url = new URL(returnUrl);
    url.searchParams.set('status', status);
    url.searchParams.set('order_id', orderId);
    if (paymentId) {
      url.searchParams.set('payment_id', paymentId);
    }
    
    window.location.href = url.toString();
  };
  
  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-indigo-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-sky-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Loading order details...</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Error state
  if (error && !order) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-indigo-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-red-200">
          <CardContent className="p-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Order Not Found</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            {returnUrl && (
              <Button onClick={() => window.location.href = returnUrl}>
                Return to Site
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Payment success state
  if (paymentStatus === 'success') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-emerald-200">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-20 h-20 text-emerald-500 mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-gray-900 mb-2">Payment Successful!</h2>
            <p className="text-gray-600 mb-4">Your payment has been processed successfully.</p>
            <div className="bg-emerald-50 rounded-lg p-4 mb-6">
              <p className="text-sm text-gray-600 mb-1">Order ID</p>
              <p className="font-mono text-sm font-semibold text-gray-900">{orderId}</p>
              <p className="text-sm text-gray-600 mt-3 mb-1">Amount Paid</p>
              <p className="text-2xl font-bold text-emerald-600">₹{order.amount}</p>
            </div>
            {returnUrl ? (
              <p className="text-sm text-gray-500">Redirecting you back...</p>
            ) : (
              <Button onClick={() => navigate('/')} className="mt-4">
                Go to Homepage
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Already paid state
  if (paymentStatus === 'already_paid') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-sky-50 to-indigo-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-sky-200">
          <CardContent className="p-8 text-center">
            <CheckCircle className="w-16 h-16 text-sky-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Already Paid</h2>
            <p className="text-gray-600 mb-6">This order has already been paid.</p>
            {returnUrl && (
              <Button onClick={() => window.location.href = returnUrl}>
                Return to Site
              </Button>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Payment failed state
  if (paymentStatus === 'failed') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-red-200">
          <CardContent className="p-8 text-center">
            <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Payment Failed</h2>
            <p className="text-gray-600 mb-6">{error || 'Something went wrong. Please try again.'}</p>
            <div className="flex gap-3 justify-center">
              <Button variant="outline" onClick={() => setPaymentStatus(null)}>
                Try Again
              </Button>
              {returnUrl && (
                <Button onClick={() => redirectToReturnUrl('failed')}>
                  Return to Site
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  // Main payment page
  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-6">
          <h1 className="text-2xl font-semibold text-gray-900 mb-1">Complete Your Payment</h1>
          <p className="text-sm text-gray-500">Secure checkout powered by Razorpay</p>
        </div>
        
        {/* Payment Card */}
        <Card className="shadow-sm border border-gray-200 mb-4">
          <CardContent className="p-6">
            {/* Amount */}
            <div className="text-center mb-6 pb-6 border-b">
              <p className="text-sm text-gray-600 mb-2">Amount to Pay</p>
              <p className="text-4xl font-bold text-gray-900">₹{order.amount}</p>
            </div>
            
            {/* Order Details - Minimal */}
            <div className="space-y-3 mb-6">
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Order ID</span>
                <span className="font-mono text-xs text-gray-900">{order.order_id}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-gray-600">Customer</span>
                <span className="text-gray-900">{order.customer.name}</span>
              </div>
            </div>
            
            {/* Payment Button - Primary */}
            <Button 
              onClick={handlePayNow}
              disabled={processing}
              className="w-full h-12 text-base font-semibold bg-blue-600 hover:bg-blue-700 text-white mb-3"
            >
              {processing ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processing...
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Pay Now
                </span>
              )}
            </Button>
            
            {/* Payment Methods Info */}
            <div className="text-center">
              <p className="text-xs text-gray-500 mb-3">Accepted Payment Methods</p>
              <div className="flex justify-center items-center gap-3 flex-wrap">
                <span className="text-xs bg-gray-100 px-3 py-1 rounded font-medium text-gray-700">UPI</span>
                <span className="text-xs bg-gray-100 px-3 py-1 rounded font-medium text-gray-700">Cards</span>
                <span className="text-xs bg-gray-100 px-3 py-1 rounded font-medium text-gray-700">Wallets</span>
                <span className="text-xs bg-gray-100 px-3 py-1 rounded font-medium text-gray-700">Net Banking</span>
              </div>
            </div>
          </CardContent>
        </Card>
        
        {/* Cancel Link */}
        {returnUrl && (
          <div className="text-center">
            <button 
              onClick={() => window.location.href = returnUrl}
              className="text-sm text-gray-600 hover:text-gray-900 underline"
            >
              Cancel and return
            </button>
          </div>
        )}
        
        {/* Security Info */}
        <div className="mt-6 p-4 bg-white rounded-lg border border-gray-200">
          <div className="flex items-start gap-3">
            <div className="text-green-600 mt-0.5">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <div>
              <p className="text-sm font-medium text-gray-900 mb-1">Secure Payment</p>
              <p className="text-xs text-gray-600">Your payment information is encrypted and secure. We never store your card details.</p>
            </div>
          </div>
        </div>
        
        {/* Footer */}
        <div className="text-center mt-6">
          <p className="text-xs text-gray-500">Powered by F Square Media</p>
        </div>
      </div>
    </div>
  );
}
